import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api-typed";
import { chunk, MAX_FREE_BUSY_USERS, withinServerRange } from "@/lib/calendar/event-times";
import type { Schemas } from "@/types/api";

// ─── Salas ocupadas, disponibilidade e convites ─────────────────────────────

/** A reserva que impede outra na mesma sala — o que o servidor devolve num 409 `room_occupied`. */
/** `id` é null quando quem pergunta não pode ver o evento que ocupa a sala — nesse caso não há nada a abrir. */
export type RoomConflict = Schemas["RoomConflictRead"];

export interface RoomAvailabilityParams {
  room_id: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  exclude_event_id?: string | null;
  recurrence_rule?: string | null;
}


/** "10:00–11:00" ou "todo o dia" — como se diz quando uma reserva ocupa a sala. */
export function conflictWhen(c: Pick<RoomConflict, "start_date" | "end_date" | "start_time" | "end_time">): string {
  if (!c.start_time) return c.start_date === c.end_date ? "todo o dia" : `${c.start_date} a ${c.end_date}`;
  return `${c.start_time.slice(0, 5)}–${(c.end_time ?? c.start_time).slice(0, 5)}`;
}

/** A frase única para "sala ocupada" — na criação, na edição, no arrasto e no 409 do servidor. */
export function roomOccupiedMessage(c: RoomConflict): string {
  // Quem não pode ver o evento que ocupa a sala recebe o título "Reservada": não se inventa um nome.
  if (c.title === "Reservada") return `Sala ocupada (${conflictWhen(c)}): já está reservada.`;
  return `Sala ocupada por "${c.title}" (${conflictWhen(c)}).`;
}

/** Lê o 409 `room_occupied` de um erro da API; `null` para qualquer outro erro. */
export function roomConflictFromError(err: unknown): RoomConflict | null {
  const e = err as { status?: number; detail?: { code?: string; conflict?: RoomConflict } } | null;
  if (e?.status === 409 && e.detail?.code === "room_occupied" && e.detail.conflict) return e.detail.conflict;
  return null;
}

/** Mensagem para mostrar ao utilizador: a do conflito, se for um, senão a do erro. */
export function calendarErrorMessage(err: unknown, fallback: string): string {
  const conflict = roomConflictFromError(err);
  if (conflict) return roomOccupiedMessage(conflict);
  return err instanceof Error && err.message ? err.message : fallback;
}

/** `null` não consulta. O servidor é a verdade; isto só adianta o aviso enquanto se preenche o formulário. */
export const useRoomAvailability = (params: RoomAvailabilityParams | null) =>
  useQuery({
    queryKey: ["room-availability", params],
    enabled: params !== null && withinServerRange(params.start_date, params.end_date),
    queryFn: () => api.get("/hr/calendar/rooms/availability", { query: params! }),
    staleTime: 10_000,
  });


export type FreeBusyParams = Schemas["FreeBusyRequest"];

/** Quem dos convidados já tem algo nesse intervalo. Só avisa: nunca bloqueia. */
export const useFreeBusy = (params: FreeBusyParams | null) =>
  useQuery({
    queryKey: ["free-busy", params],
    // O servidor aceita até 50 pessoas e 366 dias por pergunta: acima disso nem se pergunta (é um aviso, não uma regra).
    enabled: params !== null && params.user_ids.length > 0 && withinServerRange(params.start_date, params.end_date),
    queryFn: async () => {
      const blocos = chunk(params!.user_ids, MAX_FREE_BUSY_USERS);
      const respostas = await Promise.all(blocos.map((user_ids) => api.post("/hr/calendar/free-busy", { body: { ...params!, user_ids } })));
      return respostas.flat();
    },
    staleTime: 10_000,
  });

export const PENDING_INVITES_KEY = ["calendar-events", "pending-invites"] as const;

/**
 * Quantos convites por responder — o número do selo do Calendário na sidebar.
 * Atualiza sozinho (60 s e ao voltar à janela).
 * `enabled=false` não consulta: em perfis sem os módulos do calendário a rota dá 404.
 */
export const usePendingInvitesCount = (enabled = true) =>
  useQuery({
    queryKey: PENDING_INVITES_KEY,
    enabled,
    queryFn: async () => (await api.get("/hr/calendar/invites/pending-count"))?.count ?? 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

/** Um evento pelo id — para abrir o que veio de uma notificação (`?evento=`) quando ainda não está na lista. */
export const useCalendarEvent = (id: string | null) =>
  useQuery({
    queryKey: ["calendar-events", "one", id],
    enabled: !!id,
    queryFn: () => api.get("/hr/calendar/events/{event_id}", { path: { event_id: id! } }),
    retry: false,
  });
